/**
 * Notification Service for Real-time Notifications
 * Manages SSE connection and notification state
 */

import { API_BASE_URL, authenticatedFetch } from '../config/api';

export interface RealtimeNotification {
  id: number;
  userId: string;
  type: string;
  category: string;
  title: string;
  message: string;
  read: boolean;
  data?: any;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationStats {
  total: number;
  unread: number;
  pending: number;
  lastUpdated: Date;
  cacheValid: boolean;
}

type NotificationListener = (notification: RealtimeNotification) => void;
type StatsListener = (stats: NotificationStats) => void;

class NotificationService {
  private eventSource: EventSource | null = null;
  private listeners: Set<NotificationListener> = new Set();
  private statsListeners: Set<StatsListener> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectDelay = 3000; // 3 seconds
  private stats: NotificationStats = {
    total: 0,
    unread: 0,
    pending: 0,
    lastUpdated: new Date(0),
    cacheValid: false
  };
  private isConnecting = false;
  private lastConnectionTime = 0;
  private minimumReconnectInterval = 5000; // 5 seconds minimum between reconnect attempts
  private tokenRefreshListener: ((event: CustomEvent) => void) | null = null;
  private tokenExpiryTimer: ReturnType<typeof setTimeout> | null = null;
  private currentToken: string | null = null;
  private pendingConnect = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  private devLog(...args: unknown[]) {
    if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_NOTIFICATIONS === 'true') {
      console.log(...args);
    }
  }

  constructor() {
    // Listen for token refresh events
    if (typeof window !== 'undefined') {
      this.tokenRefreshListener = (event: CustomEvent) => {
        const { token } = event.detail;
        if (token) {
          this.pendingConnect = false;
          this.reconnectWithNewToken(token);
        }
      };
      window.addEventListener('tokenRefreshed', this.tokenRefreshListener as EventListener);
    }
  }

  /**
   * Check if token is valid and not near expiry
   */
  private isTokenValid(token: string): boolean {
    if (!token || typeof token !== 'string') {
      return false;
    }
    
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }
      
      const payload = JSON.parse(atob(parts[1]));
      
      if (!payload.exp) {
        return false;
      }
      
      const expiry = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = expiry - now;
      
      // Token should have at least 2 minutes left
      return timeUntilExpiry > 2 * 60 * 1000;
    } catch (error) {
      console.error('NotificationService: Error parsing token:', error);
      return false;
    }
  }

  /**
   * Get the expiry time (ms) from a JWT token
   */
  private getTokenExpiry(token: string): number | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1]));
      return payload.exp ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  /**
   * Schedule a proactive reconnection before the current token expires.
   * Reconnects 2 minutes before expiry with a fresh token from storage.
   */
  private scheduleTokenRefresh(token: string) {
    // Clear any existing timer
    if (this.tokenExpiryTimer) {
      clearTimeout(this.tokenExpiryTimer);
      this.tokenExpiryTimer = null;
    }

    const expiry = this.getTokenExpiry(token);
    if (!expiry) return;

    // Reconnect 2 minutes before the token expires
    const reconnectAt = expiry - 2 * 60 * 1000;
    const delay = reconnectAt - Date.now();

    if (delay <= 0) {
      this.tryReconnectWithStoredToken();
      return;
    }

    this.devLog(`NotificationService: Scheduling proactive reconnect in ${Math.round(delay / 1000)}s`);
    this.tokenExpiryTimer = setTimeout(() => {
      this.tryReconnectWithStoredToken();
    }, delay);
  }

  /**
   * Initialize SSE connection
   */
  connect(token: string) {
    if (!token || !this.isTokenValid(token)) {
      this.pendingConnect = true;
      this.devLog('NotificationService: Deferring connect until token refresh');
      return;
    }

    this.pendingConnect = false;

    // Prevent rapid reconnection attempts
    const now = Date.now();
    if (this.isConnecting || (now - this.lastConnectionTime < this.minimumReconnectInterval)) {
      this.devLog('NotificationService: Skipping connection attempt (too soon)');
      return;
    }

    // Check if already connected and connection is still open
    if (this.eventSource && this.eventSource.readyState === EventSource.OPEN) {
      return;
    }

    // If there's a stale eventSource in a connecting/closed state, clean it up first
    if (this.eventSource) {
      this.disconnect();
    }

    this.isConnecting = true;
    this.lastConnectionTime = now;

    try {
      const url = `${API_BASE_URL}/api/notifications/stream?token=${encodeURIComponent(token)}`;
      this.devLog('NotificationService: Connecting to notification stream');

      this.eventSource = new EventSource(url);

      this.currentToken = token;

      this.eventSource.onopen = () => {
        this.reconnectAttempts = 0;
        this.isConnecting = false;
        this.scheduleTokenRefresh(token);
      };

      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('NotificationService: Error parsing message:', error);
        }
      };

      this.eventSource.onerror = () => {
        this.isConnecting = false;
        const failedToken = token;

        const freshToken = localStorage.getItem('userToken') || localStorage.getItem('adminToken');
        if (freshToken && freshToken !== failedToken && this.isTokenValid(freshToken)) {
          this.disconnect();
          this.reconnectWithNewToken(freshToken);
          return;
        }

        if (!this.isTokenValid(failedToken)) {
          this.pendingConnect = true;
          this.disconnect();
          this.reconnectAttempts = 0;
          return;
        }

        this.disconnect();

        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
          if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
          this.reconnectTimer = setTimeout(() => {
            if (!this.eventSource) {
              const latestToken = localStorage.getItem('userToken') || localStorage.getItem('adminToken');
              if (latestToken && this.isTokenValid(latestToken)) {
                this.connect(latestToken);
              }
            }
          }, delay);
        }
      };

    } catch (error) {
      console.error('NotificationService: Error creating EventSource:', error);
      this.isConnecting = false;
    }
  }

  /**
   * Disconnect SSE connection
   */
  disconnect() {
    if (this.tokenExpiryTimer) {
      clearTimeout(this.tokenExpiryTimer);
      this.tokenExpiryTimer = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.isConnecting = false;
  }

  /**
   * Handle incoming SSE messages
   */
  private handleMessage(data: any) {
    switch (data.type) {
      case 'connected':
        this.devLog('NotificationService: Connection confirmed at', data.timestamp);
        break;

      case 'ping':
        // Heartbeat - connection is alive
        break;

      case 'notification':
        // New notification received
        this.handleNotification(data.notification);
        break;

      case 'stats':
        // Stats update received
        this.handleStatsUpdate(data.stats);
        break;

      default:
        this.devLog('NotificationService: Unknown message type:', data.type);
    }
  }

  /**
   * Handle new notification
   */
  private handleNotification(notification: RealtimeNotification) {
    this.devLog('NotificationService: New notification:', notification);

    // Notify all listeners
    this.listeners.forEach(listener => {
      try {
        listener(notification);
      } catch (error) {
        console.error('NotificationService: Error in notification listener:', error);
      }
    });

    // Update stats
    this.stats.unread++;
    this.stats.total++;
    this.stats.pending++;
    this.stats.lastUpdated = new Date();
    this.notifyStatsListeners();
  }

  /**
   * Handle stats update
   */
  private handleStatsUpdate(stats: any) {
    this.stats = {
      ...stats,
      lastUpdated: new Date(stats.lastUpdated || Date.now()),
      cacheValid: true
    };
    this.notifyStatsListeners();
  }

  /**
   * Apply stats returned by mark-read / mark-all / delete APIs.
   */
  applyServerStats(stats: Partial<NotificationStats> | null | undefined) {
    if (!stats) return;
    this.handleStatsUpdate(stats);
  }

  /**
   * Notify all stats listeners
   */
  private notifyStatsListeners() {
    this.statsListeners.forEach(listener => {
      try {
        listener(this.stats);
      } catch (error) {
        console.error('NotificationService: Error in stats listener:', error);
      }
    });
  }

  /**
   * Add a notification listener
   */
  addListener(listener: NotificationListener): () => void {
    this.listeners.add(listener);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Add a stats listener
   */
  addStatsListener(listener: StatsListener): () => void {
    this.statsListeners.add(listener);

    // Send current stats immediately
    listener(this.stats);

    // Return unsubscribe function
    return () => {
      this.statsListeners.delete(listener);
    };
  }

  /**
   * Get current stats
   */
  getStats(): NotificationStats {
    return { ...this.stats };
  }

  /**
   * Optimistically zero the unread badge after mark-all-read.
   */
  optimisticMarkAllRead() {
    this.updateStats({ unread: 0, pending: 0 });
  }

  /**
   * Optimistically decrement the unread badge after marking one or more as read.
   */
  optimisticMarkRead(count = 1) {
    const unread = Math.max(0, (this.stats.unread || 0) - count);
    this.updateStats({ unread, pending: unread });
  }

  /**
   * Update local stats (e.g., after marking as read)
   */
  updateStats(newStats: Partial<NotificationStats>) {
    this.stats = {
      ...this.stats,
      ...newStats,
      lastUpdated: new Date()
    };
    this.notifyStatsListeners();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN;
  }

  /**
   * Get connection state
   */
  getConnectionState(): 'connected' | 'connecting' | 'disconnected' {
    if (this.isConnecting) return 'connecting';
    if (this.eventSource && this.eventSource.readyState === EventSource.OPEN) return 'connected';
    return 'disconnected';
  }

  /**
   * Refresh stats from API (useful when SSE might have missed updates)
   */
  async refreshStats(): Promise<void> {
    try {
      const token = localStorage.getItem('userToken') || localStorage.getItem('adminToken');
      if (!token) {
        return;
      }

      const response = await authenticatedFetch(`${API_BASE_URL}/api/notifications/stats`);

      if (response.ok) {
        const stats = await response.json();
        this.handleStatsUpdate(stats);
        this.devLog('NotificationService: Stats refreshed from API');
      } else {
        console.error('NotificationService: Failed to refresh stats:', response.status);
      }
    } catch (error) {
      console.error('NotificationService: Error refreshing stats:', error);
    }
  }

  /**
   * Check if connecting
   */
  isConnectingState(): boolean {
    return this.isConnecting;
  }

  /**
   * Reconnect with a new token (useful after token refresh)
   * This will disconnect the current connection and establish a new one with the fresh token
   */
  reconnectWithNewToken(newToken: string) {
    if (this.eventSource) {
      this.disconnect();
    }

    this.reconnectAttempts = 0;
    this.lastConnectionTime = 0;
    this.pendingConnect = false;

    setTimeout(() => {
      if (this.isTokenValid(newToken)) {
        this.connect(newToken);
      } else {
        this.pendingConnect = true;
      }
    }, 300);
  }

  tryReconnectWithStoredToken() {
    const token = localStorage.getItem('userToken') || localStorage.getItem('adminToken');
    if (token && this.isTokenValid(token)) {
      this.reconnectWithNewToken(token);
      return true;
    }
    this.pendingConnect = true;
    return false;
  }

  /**
   * Cleanup - remove event listeners
   */
  cleanup() {
    if (typeof window !== 'undefined' && this.tokenRefreshListener) {
      window.removeEventListener('tokenRefreshed', this.tokenRefreshListener as EventListener);
      this.tokenRefreshListener = null;
    }
    this.currentToken = null;
    this.disconnect();
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;
